import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { UserCog, LogOut } from 'lucide-react';

/**
 * ImpersonationBanner — sticky amber ribbon shown whenever an admin is
 * "viewing as" another user via AuthContext.startMasquerade().
 *
 * Reads from context (not localStorage) so the same in-memory token
 * that powers axios headers is the source of truth. "Return to admin"
 * restores the stashed admin token via exitMasquerade() and bounces
 * to /.
 */
const ImpersonationBanner = () => {
  const { isMasquerading, masqueradeUser, exitMasquerade } = useAuth();
  if (!isMasquerading || !masqueradeUser) return null;

  const returnToAdmin = () => {
    exitMasquerade();
    toast.success('Restored admin session.');
    // Nudge to the home page so the admin lands somewhere they have
    // full access — otherwise the impersonated URL (e.g. /my-domain)
    // stays in the address bar and the admin might get 403s trying to
    // interact with it before the next route change.
    setTimeout(() => { window.location.href = '/'; }, 100);
  };

  return (
    <div
      className="sticky top-0 z-40 w-full bg-amber-500 text-slate-900 px-4 py-2 shadow-md flex items-center justify-center gap-3"
      data-testid="impersonation-banner"
    >
      <UserCog className="w-4 h-4" />
      <p className="text-xs md:text-sm font-semibold">
        Viewing as <span className="font-bold">{masqueradeUser.name || masqueradeUser.email}</span>
        <span className="mx-1.5 text-slate-800/60">·</span>
        <span className="uppercase tracking-wider text-[10px] text-slate-800/70">
          {(masqueradeUser.role || '').replace('domain_', '')}
        </span>
      </p>
      <button
        onClick={returnToAdmin}
        className="ml-2 text-xs font-bold px-3 py-1 rounded-full bg-slate-900 text-amber-300 hover:bg-slate-800 inline-flex items-center gap-1.5"
        data-testid="impersonation-return"
      >
        <LogOut className="w-3 h-3" /> Return to admin
      </button>
    </div>
  );
};

export default ImpersonationBanner;
